'use client';

import { useState, useEffect } from 'react';
import { visaModell } from '@/lib/fordonsnamn';
import { Link } from '@/components/LocaleLink';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { Search, Car, Package, Truck, Star, ShieldCheck, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCartStore } from '@/stores/cart';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';
import { RecentVehiclesChips } from '@/components/RecentVehiclesChips';
import { ANTAL_TEXT } from '@/lib/artikelantal';

const RegistrationLookup = dynamic(
  () => import('@/components/RegistrationLookup').then((m) => ({ default: m.RegistrationLookup })),
  { ssr: false, loading: () => <RegLookupSkeleton /> }
);
const VinLookup = dynamic(
  () => import('@/components/VinLookup').then((m) => ({ default: m.VinLookup })),
  { ssr: false, loading: () => <RegLookupSkeleton /> }
);

function RegLookupSkeleton() {
  return (
    <div className="flex gap-2">
      <div className="flex-1 h-14 rounded-lg bg-white/20 animate-pulse" />
      <div className="w-32 h-14 rounded-lg bg-white/20 animate-pulse" />
    </div>
  );
}

interface Props {
  ratingValue?: number;
  reviewCount?: number;
  articleCount?: string;
}

export function HomeHeroV2({ ratingValue, reviewCount, articleCount }: Props) {
  const { locale } = useLocale();
  const isSe = locale === 'sv';
  const [tab, setTab] = useState<'reg' | 'vin'>('reg');
  const [mounted, setMounted] = useState(false);
  const selectedVehicle = useCartStore((state) => state.selectedVehicle);
  const clearVehicle = useCartStore((state) => state.clearVehicle);

  useEffect(() => setMounted(true), []);

  // Förhandsstilen från <head> får INTE tas bort i samma effekt som sätter
  // mounted. setMounted schemalägger en ny rendering, så mellan borttagningen
  // och att React hinner sätta klasserna finns en frame där den höga hjälten
  // syns igen — mätt som ett skift på 0,667 där innehållet flyttades 274 px.
  // Genom att bero på `mounted` körs det här först när rätt klasser sitter.
  useEffect(() => {
    if (mounted) document.getElementById('af-hero-pre')?.remove();
  }, [mounted]);

  const steps = [
    {
      n: 1,
      icon: Car,
      title: isSe ? 'Sök din bil' : 'Find your car',
      body: isSe ? 'Skriv regnummer eller VIN — vi visar exakt vilka delar som passar' : 'Enter your reg or VIN — we show only parts that fit',
    },
    {
      n: 2,
      icon: Package,
      title: isSe ? 'Välj rätt del' : 'Pick the part',
      body: isSe ? 'Vi guidar dig till bromsar, olja, filter eller den del du behöver' : 'We guide you to brakes, oil, filters, or what you need',
    },
    {
      n: 3,
      icon: Truck,
      title: isSe ? 'Beställ — vi levererar' : 'Order — we deliver',
      body: isSe ? '2–5 dagars hemleverans, 30 dagars öppet köp, säker betalning' : '2–5 day home delivery, 30-day returns, secure payment',
    },
  ];

  // Fram till 2026-09-08 returnerade komponenten det kompakta läget tidigt,
  // efter att `mounted` blivit sant. Servern skickade alltså alltid den höga
  // hjälten, och för en besökare med vald bil byttes den mot ett mycket lägre
  // block först efter hydrering — allt nedanför hoppade upp. Mätt CLS 0,73 mot
  // 0,004 utan vald bil.
  //
  // Nu ligger BÅDA varianterna i HTML:en och en inline-stil i <head> (se
  // heroScript i layout.tsx) väljer före första målningen, precis som
  // cookie-rutan gör. Ingen hydreringskonflikt: React-trädet är identiskt på
  // server och klient, bara en <style> skiljer.
  const harBil = mounted && !!selectedVehicle;

  return (
    <>
      <section className={`af-hero-vehicle ${harBil ? '' : 'hidden'} relative bg-gradient-to-br from-[#0f2e5a] via-[#143670] to-[#0f2e5a] text-white py-8 md:py-10`}>
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5">
            <div className="flex items-center gap-3 min-w-0">
              <div className="shrink-0 h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center">
                <Car className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-white/70">
                  {isSe ? 'Du letar delar för' : 'Searching parts for'}
                </p>
                {/* Höjden hålls av ikonen (48 px) och truncate ger alltid
                    en rad, så texten kan fyllas i efter hydrering utan skift. */}
                <p className="font-semibold text-lg truncate">
                  {selectedVehicle
                    ? `${selectedVehicle.manufacturer} ${visaModell(selectedVehicle.model)}${selectedVehicle.cartype ? ` · ${selectedVehicle.cartype}` : ''}`
                    : '\u00a0'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* Direkt till delarna — startsidan med vald bil var annars bara ett
                  extra tryck på vägen (ägaren 2026-09-16). */}
              {selectedVehicle && (
                <Link
                  href={`/fordon/${selectedVehicle.ktype}`}
                  className="inline-flex items-center h-10 px-4 rounded-md bg-amber-400 text-[#0f2e5a] font-semibold hover:bg-amber-300 transition-colors"
                >
                  {isSe ? 'Visa alla delar' : 'Show all parts'}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              )}
              <Button
                variant="secondary"
                onClick={clearVehicle}
                className="bg-white text-[#0f2e5a] hover:bg-white/90 shrink-0"
              >
                <X className="h-4 w-4 mr-2" />
                {isSe ? 'Byt bil' : 'Change car'}
              </Button>
            </div>
          </div>
        </div>
      </section>


      <section className={`af-hero-full ${harBil ? 'hidden' : ''} relative overflow-hidden bg-gradient-to-br from-[#0f2e5a] via-[#143670] to-[#0f2e5a] text-white`}>
      {/* Subtle pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }}
      />
      {/* Tricolor accent at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-1 flex">
        <div className="flex-1 bg-[#0055A4]" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-[#EF4135]" />
      </div>

      <div className="relative container mx-auto px-4 py-10 sm:py-14 md:py-20">
        {/* Headline */}
        <div className="text-center mb-6 md:mb-8 max-w-3xl mx-auto">
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-3">
            {isSe ? (
              <>
                Hitta rätt reservdel till din bil
                <br />
                <span className="text-amber-300">på 3 enkla steg</span>
              </>
            ) : (
              <>
                Find the right car part
                <br />
                <span className="text-amber-300">in 3 simple steps</span>
              </>
            )}
          </h1>
          <p className="text-base sm:text-lg text-white/80 max-w-xl mx-auto">
            {isSe
              ? 'Skriv ditt registreringsnummer så visar vi bara delar som garanterat passar din bil.'
              : 'Enter your registration number and we show only parts that fit your car.'}
          </p>
        </div>

        {/* Search card */}
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl p-4 sm:p-6">
          {/* Tabs */}
          <div className="flex rounded-lg border overflow-hidden mb-4">
            <button
              type="button"
              onClick={() => setTab('reg')}
              className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                tab === 'reg'
                  ? 'bg-[#0f2e5a] text-white'
                  : 'bg-gray-50 text-gray-600 hover:text-gray-900'
              }`}
            >
              {isSe ? 'Registreringsnummer' : 'Registration plate'}
            </button>
            <button
              type="button"
              onClick={() => setTab('vin')}
              className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                tab === 'vin'
                  ? 'bg-[#0f2e5a] text-white'
                  : 'bg-gray-50 text-gray-600 hover:text-gray-900'
              }`}
            >
              {isSe ? 'Chassinummer (VIN)' : 'Chassis number (VIN)'}
            </button>
          </div>

          {tab === 'reg' ? <RegistrationLookup onSwitchToVin={() => setTab('vin')} /> : <VinLookup />}

          {/* Fallback: search by part name */}
          <div className="mt-4 pt-4 border-t text-center">
            <Link
              href="/katalog"
              className="inline-flex items-center text-sm font-medium text-[#0f2e5a] hover:text-[#0055A4] hover:underline"
            >
              {isSe ? 'Vet du redan vad du söker? Bläddra i katalogen' : 'Already know what you need? Browse the catalog'}
              <ChevronRight className="h-4 w-4 ml-0.5" />
            </Link>
          </div>
        </div>

        {/* Recent vehicles — one-tap to reactivate a previous search */}
        <RecentVehiclesChips variant="dark" limit={4} action="navigate" />

        {/* 3 steps */}
        <div className="mt-10 md:mt-14 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
            {steps.map((s, i) => (
              <div key={s.n} className="relative">
                <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl p-5 h-full">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="shrink-0 h-10 w-10 rounded-full bg-amber-400 text-[#0f2e5a] font-bold flex items-center justify-center text-lg">
                      {s.n}
                    </div>
                    <s.icon className="h-6 w-6 text-amber-300" />
                  </div>
                  <h2 className="font-semibold text-lg mb-1.5">{s.title}</h2>
                  <p className="text-sm text-white/90 leading-relaxed">{s.body}</p>
                </div>
                {/* Arrow between cards (desktop) */}
                {i < steps.length - 1 && (
                  <ChevronRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 h-6 w-6 text-amber-300/60" aria-hidden />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-10 md:mt-12 max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm">
            {ratingValue && reviewCount ? (
              <a href="#omdomen" className="flex items-center gap-2 hover:text-amber-300 transition-colors">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${i <= Math.round(ratingValue) ? 'fill-amber-400 text-amber-400' : 'text-white/30'}`}
                    />
                  ))}
                </div>
                <span className="font-semibold">{ratingValue.toFixed(1)}/5</span>
                <span className="text-white/70">({reviewCount} {isSe ? 'omdömen' : 'reviews'})</span>
              </a>
            ) : null}
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-amber-300" />
              <span>{articleCount ? `${articleCount} ${isSe ? 'reservdelar' : 'parts'}` : (isSe ? '${ANTAL_TEXT}+ reservdelar' : '470,000+ parts')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-amber-300" />
              <span>{isSe ? '2–5 dagars leverans' : '2–5 day delivery'}</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-amber-300" />
              <span>{isSe ? 'Sedan 1988' : 'Since 1988'}</span>
            </div>
          </div>
        </div>
      </div>
      </section>
    </>
  );
}
